import { Box, Fade, Stack, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import { differenceInDays, differenceInHours, subDays } from "date-fns"
import React, { useEffect, useRef, useState } from "react"
import fireSvg from '../../../assets/img/fire.svg'

export type AutoIncrementStatProps = {
    autoIncrementFromDate?: Date
    autoIncrementPercentage?: number
    autoIncrementPeriod?: undefined | 'monthly' | 'week' | 'hour'
}

const getStatValue = function (
    {
        value,
        autoIncrementFromDate,
        autoIncrementPercentage,
        autoIncrementPeriod,
    }: AutoIncrementStatProps & { value: number }
) {
    if (!autoIncrementFromDate || autoIncrementPercentage === undefined || !autoIncrementPeriod) {
        return value;
    }

    try {
        const date = autoIncrementFromDate;
        const now = new Date();
        // Align to UTC hour
        const currentHourUtc = new Date(
            Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate(),
                now.getUTCHours(),
                0, 0, 0
            )
        );

        // Create a deterministic “random” in [0,1) based on the hour
        // We use hours since epoch as a seed
        const hoursSinceEpoch = Math.floor(currentHourUtc.getTime() / (1000 * 60 * 60));
        // simple hash → fract(sin(seed)*10000)
        const seeded = Math.abs(Math.sin(hoursSinceEpoch) * 10000) % 1;

        // Map to ±30% of autoIncrementPercentage
        const variation = autoIncrementPercentage * ((seeded * 2 - 1) * 0.3);
        const effectivePercentage = autoIncrementPercentage + variation;

        let periodsCount: number;
        switch (autoIncrementPeriod) {
            case 'monthly': {
                const dayDiff = differenceInDays(currentHourUtc, date);
                periodsCount = dayDiff / (365 / 12);
                break;
            }
            case 'week': {
                const dayDiff = differenceInDays(currentHourUtc, date);
                periodsCount = dayDiff / 7;
                break;
            }
            case 'hour': {
                periodsCount = differenceInHours(currentHourUtc, date);
                break;
            }
            default: {
                // daily as fallback
                const dayDiff = differenceInDays(currentHourUtc, date);
                periodsCount = dayDiff;
                break;
            }
        }

        return value * (1 + (effectivePercentage / 100) * periodsCount);
    } catch (e) {
        console.error(e);
        return value;
    }
};

type Props = AutoIncrementStatProps & {
    value: number
}

/**
 * Returns the UTC datetime for exactly 7 days ago from now.
 */
export function getSevenDaysAgoUtc(): Date {
    const now = new Date()
    const sevenDaysAgo = subDays(now, 7)
    return new Date(
        Date.UTC(
            sevenDaysAgo.getUTCFullYear(),
            sevenDaysAgo.getUTCMonth(),
            sevenDaysAgo.getUTCDate(),
            sevenDaysAgo.getUTCHours(),
            0,
            0,
            0
        )
    )
}

const CounterBox = styled('div')(({ theme }) => ({
    color: '#c10b00',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    borderRadius: '15px',
}))

export const PurchaseCounter = (props: Props) => {
    const [statValue, setStatValue] = useState<string>('')
    const boxRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        // Example: for weekly period, use 7 days ago. For hourly, you might use 1 hour ago, etc.
        // const baseDate =
        //     props.autoIncrementPeriod === 'hour'
        //         ? subDays(new Date(), 0) // or new Date(Date.now() - 1000 * 60 * 60)
        //         : getSevenDaysAgoUtc()

        const baseDate = getSevenDaysAgoUtc()

        const raw = getStatValue({
            value: props.value,
            autoIncrementFromDate: baseDate,
            autoIncrementPeriod: props.autoIncrementPeriod,
            autoIncrementPercentage: props.autoIncrementPercentage ?? 4.2,
        })

        setStatValue(Math.round(raw).toLocaleString())
    }, [props])

    return (
        <Box ref={boxRef} sx={{ display: 'flex', margin: '0 auto' }}>
            <CounterBox>
                {statValue ? (
                    <Fade timeout={4000} in={true}>
                        <Stack direction="row" gap={1}>
                            <img alt="fire" src={fireSvg} style={{ height: '20px', width: '20px' }} />
                            <Stack direction="row" gap={0.5}>
                                <Typography sx={{ fontWeight: 'bold', color: 'inherit' }}>
                                    {statValue}
                                </Typography>
                                <Typography sx={{ color: 'inherit' }}>
                                    {/* {props.autoIncrementPeriod === 'hour'
                                        ? 'purchased in the last hour'
                                        : 'purchased in last 7 days'} */}
                                    purchased in last 7 days
                                </Typography>
                            </Stack>
                        </Stack>
                    </Fade>
                ) : (
                    <Box sx={{ height: '24px' }} />
                )}
            </CounterBox>
        </Box>
    )
}
